import {
  acceptCaseById,
  assignCase,
  deleteCase,
  rejectCaseById,
  reportCase,
  updateCaseStatus,
} from "../api";
import {CASE_MODAL_CONTENT} from "../components/CaseModalContent";
import {getGroupNameByPath} from "../model/caseHelpers";

type UseCaseManagementMutationsProps = {
  userToken: string;
  assigningStatus: {id: string; name: string};
  refreshCases: () => Promise<void>;
  setDialogContent: (value: CASE_MODAL_CONTENT | "") => void;
  setMessage: (value: string) => void;
  setMessageType: (value: string) => void;
};

export const useCaseManagementMutations = ({
  userToken,
  assigningStatus,
  refreshCases,
  setDialogContent,
  setMessage,
  setMessageType,
}: UseCaseManagementMutationsProps) => {
  const handleAccept = async (caseItem) => {
    if (!caseItem || !caseItem.id) return;
    setDialogContent(CASE_MODAL_CONTENT.LOADING);
    try {
      await acceptCaseById({
        token: userToken,
        acceptedCase: caseItem,
      });
      await refreshCases();
      setMessage("已接收此案件");
      setMessageType("success");
      setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
    } catch (error) {
      console.error("案件接受失敗:", error);
    }
  };

  const handleReject = async (caseItem) => {
    if (!caseItem?.id) return;
    setDialogContent(CASE_MODAL_CONTENT.LOADING);
    try {
      await rejectCaseById({
        token: userToken,
        rejectedCase: caseItem,
      });
      await refreshCases();
      setMessage("已拒絕此案件");
      setMessageType("success");
      setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
    } catch (error) {
      console.error("案件拒絕失敗:", error);
    }
  };

  const handleAssign = async (caseItem, organization) => {
    if (!caseItem || !caseItem.id || !organization) return;
    setDialogContent(CASE_MODAL_CONTENT.LOADING);
    try {
      const response = await assignCase({
        token: userToken,
        organizationId: organization.id,
        assignedCase: caseItem,
        category: getGroupNameByPath(caseItem.category),
      });
      if (response.message === "User dependencies") {
        setMessage("您還沒為此機構新增機構人員，請新增後再派案");
        setMessageType("error");
        setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
      } else {
        setMessage("案件已派發");
        setMessageType("success");
        setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
        await refreshCases();
      }
    } catch (error) {
      console.error("案件派發失敗:", error);
    }
  };

  const handleDelete = async (caseItem) => {
    setDialogContent(CASE_MODAL_CONTENT.LOADING);
    try {
      await deleteCase({token: userToken, id: caseItem?.id});
      await refreshCases();
      setMessageType("success");
      setMessage("已成功刪除");
      setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
    } catch (error) {
      console.error("案件刪除失敗:", error);
    }
  };

  const handleReport = async (caseItem, data) => {
    setDialogContent(CASE_MODAL_CONTENT.LOADING);
    try {
      await reportCase({token: userToken, reportedCase: caseItem, report: data});
      await refreshCases();
      setMessageType("success");
      setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
    } catch (error) {
      console.error("案件回報失敗:", error);
    }
  };

  const handleAssigningStatus = async (caseItem) => {
    setDialogContent(CASE_MODAL_CONTENT.LOADING);
    try {
      await updateCaseStatus({token: userToken, id: caseItem.id, status: assigningStatus.id});
      await refreshCases();
      setMessage("狀態已變更");
      setMessageType("success");
      setDialogContent(CASE_MODAL_CONTENT.MESSAGE);
    } catch (error) {
      console.error("變更失敗:", error);
    }
  };

  return {
    handleAccept,
    handleReject,
    handleAssign,
    handleDelete,
    handleReport,
    handleAssigningStatus,
  };
};
